// Carer-parity S3a — full parity with coordinator visit-report flow.
// The bespoke 844-line chip-form was replaced by <VisitReportWizard
// submissionChannel="mobile">. The wizard reads the active BearerProvider's
// fetcher (carerFetch in this subtree, with silent refresh on 401), so the
// 5–15 min mobile fill window survives access-token expiry without bouncing
// the carer to login.
//
// TASK-N6 (commit follow-up to Carer-parity Sub-4 = 4b deferral) — closed
// 2026-05-05. The page now consumes the lifted useVisitReportData hook
// (apps/web/src/hooks/useVisitReportData.ts), which fires the same 2-query
// flow as VisitReportPage on coordinator: ["sr", id] then
// ["patient", patient_id], routed through useFetcher() so the carer subtree
// gets carerFetch's silent refresh on 401. baselineMobility +
// isAtWanderingRisk now reach the wizard with full coordinator-side fidelity;
// per N1a-i, carerApi.getVisit is dropped and the subtitle reads
// patientData.full_name. See docs/projects/task-n6-mobility-fetch-parity.md.

import { useEffect, type JSX } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { VisitType } from "@hcm/core";
import { carerApi, CarerApiError } from "../../lib/carerApi.js";
import { VisitReportWizard } from "../../components/VisitReportWizard.js";
import { CarerTopBar } from "./CarerTopBar.js";
import { CarerNotificationBell } from "./CarerNotificationBell.js";
import { useVisitReportData } from "../../hooks/useVisitReportData.js";

function ErrorState({ message, onBack }: { message: string; onBack: () => void }): JSX.Element {
  return (
    <>
      <CarerTopBar
        title="Visit Report"
        onBack={onBack}
        rightSlot={<CarerNotificationBell />}
      />
      <main className="max-w-lg mx-auto px-4 py-4">
        <div role="alert" className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-red-700 text-sm">{message}</p>
        </div>
      </main>
    </>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div
        role="status"
        aria-label="Loading"
        className="w-8 h-8 border-4 border-navy-700 border-t-transparent rounded-full animate-spin"
      />
    </div>
  );
}

interface ReadyStateProps {
  id: string;
  srData: NonNullable<ReturnType<typeof useVisitReportData>["srData"]>;
  patientData: NonNullable<ReturnType<typeof useVisitReportData>["patientData"]>;
  isAtWanderingRisk: boolean;
  navigate: ReturnType<typeof useNavigate>;
}
function ReadyState({ id, srData, patientData, isAtWanderingRisk, navigate }: ReadyStateProps): JSX.Element {
  const subtitle = patientData.full_name?.trim() || `Patient …${srData.patient_id.slice(-6)}`;
  return (
    <>
      <CarerTopBar
        title="Visit Report"
        subtitle={subtitle}
        onBack={() => navigate(`/carer/visit/${id}`)}
        rightSlot={<CarerNotificationBell />}
      />
      <main className="max-w-lg mx-auto px-4 py-4 pb-32">
        <VisitReportWizard
          srId={id}
          patientId={srData.patient_id}
          baselineMobility={patientData.baseline_mobility ?? null}
          initialVisitType={(srData.visit_type as VisitType | null | undefined) ?? null}
          isAtWanderingRisk={isAtWanderingRisk}
          submissionChannel="mobile"
          onSubmitted={() => { navigate("/carer", { replace: true }); }}
        />
      </main>
    </>
  );
}

export function CarerVisitReportPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { ready, srData, patientData, isAtWanderingRisk, isError, error } =
    useVisitReportData(id ?? "");

  // Long-form session keepalive — the wizard fill window is 5–15 min, but the
  // Redis carer-inactivity TTL is 30 min. A 5-min heartbeat keeps the session
  // warm; carerFetch's silent refresh handles the rare access-token expiry.
  useEffect(() => {
    const handle = window.setInterval(() => { void carerApi.keepalive(); }, 5 * 60 * 1000);
    return () => window.clearInterval(handle);
  }, []);

  if (isError || !id) {
    const message = error instanceof CarerApiError ? error.message : "Could not load visit.";
    return <ErrorState message={message} onBack={() => navigate("/carer")} />;
  }

  if (!ready || !srData || !patientData) {
    return <LoadingState />;
  }

  return (
    <ReadyState
      id={id}
      srData={srData}
      patientData={patientData}
      isAtWanderingRisk={isAtWanderingRisk}
      navigate={navigate}
    />
  );
}
